import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Spinner, Button } from "react-bootstrap";
import toastr from "toastr";
import { ArrowUp, ArrowDown, Search, Download } from "lucide-react";

interface MessageCodeRow {
    messageCode: string;
    shortDescription: string;
    messageText: string;
    isSystemMessage: boolean;
    isActive: boolean;
}

type SortKey = keyof MessageCodeRow;
type SortDir = "asc" | "desc";

const AdjudicationMessageCodesList: React.FC = () => {
    const [rows, setRows] = useState<MessageCodeRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [systemFilter, setSystemFilter] = useState<string>("ALL");
    const [statusFilter, setStatusFilter] = useState<string>("ACTIVE");
    const [sortKey, setSortKey] = useState<SortKey>("messageCode");
    const [sortDir, setSortDir] = useState<SortDir>("asc");

    useEffect(() => {
        axios
            .get("/api/Database/message-codes")
            .then((res) => {
                const data = (res.data || []).map((x: any) => ({
                    messageCode: x.messageCode,
                    shortDescription: x.shortDescription,
                    messageText: x.messageText,
                    isSystemMessage: x.isSystemMessage,
                    isActive: x.isActive,
                }));
                setRows(data);
            })
            .catch(() => {
                toastr.error("Failed to load message codes", "Error");
            })
            .finally(() => setLoading(false));
    }, []);

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        let list = rows;
        if (systemFilter !== "ALL") {
            const isSys = systemFilter === "SYSTEM";
            list = list.filter((r) => r.isSystemMessage === isSys);
        }
        if (statusFilter !== "ALL") {
            const active = statusFilter === "ACTIVE";
            list = list.filter((r) => r.isActive === active);
        }
        if (term) {
            list = list.filter(
                (r) =>
                    (r.messageCode || "").toLowerCase().includes(term) ||
                    (r.shortDescription || "").toLowerCase().includes(term) ||
                    (r.messageText || "").toLowerCase().includes(term)
            );
        }
        const sorted = [...list].sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            if (typeof av === "boolean" && typeof bv === "boolean") {
                return av === bv ? 0 : av ? -1 : 1;
            }
            const as = (av ?? "").toString().toLowerCase();
            const bs = (bv ?? "").toString().toLowerCase();
            if (as < bs) return sortDir === "asc" ? -1 : 1;
            if (as > bs) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [rows, search, systemFilter, statusFilter, sortKey, sortDir]);

    const exportCsv = () => {
        const headers = ["Code", "Short Description", "Message", "System", "Active"];
        const escape = (val: any) => {
            const s = (val ?? "").toString();
            if (/[",\r\n]/.test(s)) {
                return `"${s.replace(/"/g, '""')}"`;
            }
            return s;
        };
        const lines = [headers.join(",")];
        filtered.forEach((r) => {
            lines.push([
                escape(r.messageCode),
                escape(r.shortDescription),
                escape(r.messageText),
                escape(r.isSystemMessage ? "Yes" : "No"),
                escape(r.isActive ? "Yes" : "No"),
            ].join(","));
        });
        const csv = "﻿" + lines.join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `adjudication-message-codes-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const SortHeader = ({ label, column }: { label: string; column: SortKey }) => (
        <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort(column)}>
            {label}
            {sortKey === column && (
                <span style={{ marginLeft: 4 }}>
                    {sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                </span>
            )}
        </th>
    );

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: 40 }}>
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                <h4 style={{ margin: 0, flexGrow: 1 }}>Adjudication Message Codes</h4>
                <div style={{ position: "relative" }}>
                    <Search size={16} style={{ position: "absolute", left: 10, top: 10, color: "#888" }} />
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ paddingLeft: 32, width: 240 }}
                    />
                </div>
                <select
                    className="form-select"
                    style={{ width: 160 }}
                    value={systemFilter}
                    onChange={(e) => setSystemFilter(e.target.value)}
                >
                    <option value="ALL">All messages</option>
                    <option value="SYSTEM">System only</option>
                    <option value="MANUAL">Manual only</option>
                </select>
                <select
                    className="form-select"
                    style={{ width: 140 }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="ALL">All statuses</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                </select>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={exportCsv}
                    disabled={filtered.length === 0}
                    title="Export current view to CSV"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                    <Download size={14} />
                    CSV
                </Button>
            </div>

            <div className="dashboard-table-container">
                <table className="table table-striped table-hover">
                    <thead>
                        <tr>
                            <SortHeader label="Code" column="messageCode" />
                            <SortHeader label="Short Description" column="shortDescription" />
                            <SortHeader label="Message" column="messageText" />
                            <SortHeader label="System" column="isSystemMessage" />
                            <SortHeader label="Active" column="isActive" />
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={5} style={{ textAlign: "center", padding: 20 }}>
                                    No message codes match the current filters.
                                </td>
                            </tr>
                        ) : (
                            filtered.map((r, idx) => (
                                <tr key={idx}>
                                    <td>{r.messageCode}</td>
                                    <td>{r.shortDescription}</td>
                                    <td>{r.messageText}</td>
                                    <td>{r.isSystemMessage ? "Yes" : "No"}</td>
                                    <td>{r.isActive ? "Yes" : "No"}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: 8, color: "#666", fontSize: 13 }}>
                Showing {filtered.length} of {rows.length}
            </div>
        </div>
    );
};

export default AdjudicationMessageCodesList;
